import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';

const MedicalDeviceForm = ({ onCaptured }: { onCaptured: any }) => {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef<any>(null);
  const canvasRef = useRef<any>(null);

  const startCamera = async () => {
    try {
      let newStream = null;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: 'environment' } },
        });
      } catch (e) {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: 'user' } },
        });
      }

      setStream(newStream);
      setIsCameraActive(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('カメラへのアクセスに失敗しました。');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track: any) => track.stop());
    }
    setStream(null);
    setIsCameraActive(false);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      context.drawImage(
        videoRef.current,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
      performOCR();
    }
  };

  const performOCR = async () => {
    setIsProcessing(true);
    setError('');
    const base64Image = canvasRef.current.toDataURL('image/jpeg').split(',')[1];

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash-latest:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`,
        {
          contents: [
            {
              parts: [
                {
                  text: 'Perform OCR on this image and extract all visible text. Then, identify and extract the following information: 医療機関名 (Medical Institution Name), 患者ID (Patient ID), 製造年月日 (Manufacturing Date), 使用期限 (Expiration Period), and 製造元 (Manufacturer). Format the output as follows:\n\n医療機関名: [value]\n患者ID: [value]\n製造年月日: [value]\n使用期限: [value]\n製造元: [value]',
                },
                { inline_data: { mime_type: 'image/jpeg', data: base64Image } },
              ],
            },
          ],
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const extractedText = response.data.candidates[0].content.parts[0].text;
      parseOCRResult(extractedText);
    } catch (error) {
      console.error('Error performing OCR:', error);
      setError('OCR処理中にエラーが発生しました。');
      stopCamera();
    } finally {
      setIsProcessing(false);
      stopCamera();
    }
  };

  const parseOCRResult = (result: any) => {
    const lines = result.split('\n');
    console.log(lines);
    onCaptured(lines);
    stopCamera();
  };

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isCameraActive ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 2,
            aspectRatio: '16/9',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: '100%', marginBottom: 2, objectFit: 'cover' }}
          />
          <Stack direction='row' justifyContent='center' spacing={'8px'} sx={{ mt: 1 }}>
            <BtnOutlineGradient text='キャンセル' handleButtonClick={stopCamera}/>
            <BtnGradient text={isProcessing ? 'Loading' : '画像を読み取る'} width='120px' handleButtonClick={captureImage} disabled={isProcessing}/>
          </Stack>
        </Box>
      ) : (
        <BtnGradient
          text='画像から読み取る'
          handleButtonClick={startCamera}
          width='152px'
        />
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert
          onClose={() => setError('')}
          severity='error'
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MedicalDeviceForm;
